import React, { useCallback, useMemo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { endOfMonth, formatISO, subMonths } from "date-fns";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCanPerformWorkspaceAction } from "@hooks/permissions/useCanPerformWorkspaceAction";
import { WorkspaceAction } from "@common/userWorkspaceRoles";
import { LockPolicy } from "@common/types/lock";
import { Entity } from "@common/types/entity";
import { fullMonthFullYearFormat } from "@common/helpers/date";
import { updateLock } from "@lib/lock";
import { useLockDate } from "@api/lock.api";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useConstant } from "@hooks/useConstant";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import {
    getMonthRange,
    toDateOnly,
    toDateOnlyRange,
} from "@design-system/DatePicker/DatePicker.lib";
import type { DateOnlyRange } from "@design-system/DatePicker/DatePicker.types";
import { formatLockDateMessage } from "./formatLockDateMessage";
import styles from "./WorkspaceLock.module.scss";

function toMonthPickerValue(lockDate: Date | null): DateOnlyRange | undefined {
    return lockDate ? toDateOnlyRange(getMonthRange(lockDate)) : undefined;
}

const LockDateCellComponent: React.FC<
    CellContext<ViewTableFeatures, Entity, number>
> = ({ row }) => {
    const entity = row.original;
    const lockDate = useLockDate(entity.id);
    const canLock = useCanPerformWorkspaceAction(WorkspaceAction.LOCKS_EDIT);
    const { toast } = useToaster();

    const value = useMemo(() => toMonthPickerValue(lockDate), [lockDate]);

    const maxDate = useConstant(() =>
        formatISO(endOfMonth(subMonths(new Date(), 1)), {
            representation: "date",
        }),
    );

    const handleChange = useCallback(
        async (range: DateOnlyRange | undefined) => {
            const newLockDateValue = range?.to ?? null;
            const currentLockDateValue = lockDate
                ? (toDateOnly(lockDate) ?? null)
                : null;

            if (newLockDateValue === currentLockDateValue) {
                return;
            }

            await updateLock({
                entityId: entity.id,
                date: newLockDateValue,
                policy: LockPolicy.ALL,
            });

            toast(formatLockDateMessage(newLockDateValue));
        },
        [entity.id, lockDate, toast],
    );

    return (
        <ViewTableCell captureClicks>
            <DatePicker
                mode="month"
                variant="button"
                availableModes={["month"]}
                value={value}
                onValueChange={handleChange}
                maxDate={maxDate}
                displayFormat={fullMonthFullYearFormat}
                placeholder="Select month"
                disabled={!canLock}
                clearable
                showActions
                className={styles.datePickerInput}
            />
        </ViewTableCell>
    );
};

export const LockDateCell = React.memo(LockDateCellComponent);
LockDateCell.displayName = "LockDateCell";
